import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, } from '@angular/core';
import { Chartjs } from 'app/trello/chartjs.imports';
import { LabelledMatrixValues } from 'app/trello/models/charts';
import { ReadableSecondsPipe } from 'app/trello/pipes/readable-seconds.pipe';
import { Templating } from 'app/trello/templating.imports';
import { Chart, ChartOptions, ChartType, LegendOptions, TooltipOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Chartjs,
    Templating,
    ContextMenuModule,
    DialogModule,
  ],
  providers: [ReadableSecondsPipe],
  selector: 'app-heatmap-chart-with-table',
  styleUrls: ['./heatmap-chart-with-table.component.css'],
  templateUrl: './heatmap-chart-with-table.component.html',
})
export class HeatmapChartWithTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input({required: true}) chartData!: LabelledMatrixValues;

  @Input() chartContainer: HTMLElement | undefined = undefined;

  @Input() baseColor: string = '0,175,221';
  @Input() maxValue?: number;

  @Input() tableTitle: string | undefined;

  @Input() tooltipFormatter: ((x: number, y: number) => string) | undefined;

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'number';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  @ViewChild('canvasContainer') protected canvasContainer!: ElementRef;
  @ViewChild(BaseChartDirective) protected chartDirective!: BaseChartDirective;

  private chartContainerResizeObserver: ResizeObserver | undefined;

  private get chartInstance(): Chart | undefined {
    return this.chartDirective?.chart;
  }

  private initialized: boolean = false;
  private initializedView: boolean = false;

  protected readonly canvasContextMenuItems = [
    {
      label: 'View Data',
      command: () => {
        this.showDialog();
      }
    }
  ];

  protected readonly chartType: ChartType = 'bar';
  protected chartOptions!: ChartOptions;

  protected mappedChartAxisLabels: string[] = [];

  protected mappedChartDatasets: any[] = [];
  protected mappedChartDatasetsReady: boolean = false;

  protected tableDialogOpen: boolean = false;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly readableSecondsPipe: ReadableSecondsPipe,
  ) {
  }

  public ngOnInit(): void {

    this.initialized = true;

    this.prepareData();
  }

  public ngAfterViewInit(): void {

    this.initializedView = true;

    this.keepCanvasSizeSameAsChartContainer();

    this.prepareChartOptions();

    this.changeDetector.detectChanges();

    if (this.chartInstance) {
      this.chartDirective.update();
    }
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartContainer']) {
      this.keepCanvasSizeSameAsChartContainer();
    }

    if (changes['baseColor']
      || changes['chartData']
      || changes['maxValue']) {

      this.prepareData();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }

    if (changes['valueFormat']) {

      this.prepareChartOptions();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }
  }

  public ngOnDestroy(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  protected showDialog(): void {

    this.tableDialogOpen = true;

    this.changeDetector.detectChanges();
  }

  private prepareChartOptions(): void {

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      scales: {
        x: {
          display: true,
          grid: {
            display: false,
          },
          stacked: true,
          title: {
            display: false,
            text: '',
          },
          type: 'category',
        },
        y: {
          beginAtZero: true,
          display: true,
          grace: 0,
          grid: {
            display: false,
          },
          stacked: true,
          ticks: {
            autoSkip: false,
            stepSize: 1,
            count: (this.chartData.yLabels.length * 2) + 1,
            callback: (
              value,
              index,
              ticks
            ) => {

              if (index % 2 !== 1) {
                return '';
              }

              const origLabelIndex = (index - 1) / 2;

              const reverseLabelIndex = this.chartData.yLabels.length - 1 - origLabelIndex;

              return this.chartData.yLabels[reverseLabelIndex] || (reverseLabelIndex + 1);
            }
          },
          title: {
            display: false,
            text: '',
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        } as LegendOptions<'bar'>,
        tooltip: {
          xAlign: 'center',
          yAlign: 'center',
          callbacks:
            {
              title: (
                tooltipItems,
              ) => {

                const tooltipItem = tooltipItems[0];

                const xLabelIndex = tooltipItem.dataIndex;
                const yLabelIndex = tooltipItem.datasetIndex;

                if (this.tooltipFormatter) {
                  return this.tooltipFormatter(xLabelIndex, yLabelIndex);
                }

                const columnLabel = this.chartData.xLabels[xLabelIndex];

                const rowIndex = this.chartData.yLabels.length - 1 - yLabelIndex;
                const rowLabel = this.chartData.yLabels[rowIndex];

                return `${columnLabel}, ${rowLabel}`;
              },
              label:
                (
                  tooltipItem,
                ) => {

                  const columnIndex = tooltipItem.dataIndex;
                  const rowIndex = tooltipItem.datasetIndex;

                  const reversedRowIndex = this.chartData.values[columnIndex].length - 1 - rowIndex;

                  const value = this.chartData.values[columnIndex][reversedRowIndex];

                  return this.formatValue(value);
                },
            },
        } as TooltipOptions<'bar'>,
      } as any,
    };
  }

  private prepareData(): void {

    if (!this.initialized
      || !this.chartData) {
      return;
    }

    const {xLabels, yLabels, values} = this.chartData;

    const effectiveMaxValue = this.maxValue || Math.max(...this.chartData.values.flat());

    this.mappedChartAxisLabels = xLabels;

    this.mappedChartDatasets =
      yLabels.map((columnLabel, columnIndex) => {

        const columnValues =
          values
            .map((rowValues) => rowValues.slice().reverse())
            .map((rowValues) => rowValues[columnIndex]);

        const backgroundColors = columnValues.map((columnValue) => this.computeColor(columnValue, effectiveMaxValue));

        const scaffold = new Array(values.length).fill(1);

        return {
          label: columnLabel,
          data: scaffold, // equal height stack elements
          backgroundColor: backgroundColors,
          barPercentage: 1,
          categoryPercentage: 1,
          borderColor: 'transparent',
          borderSkipped: false,
          borderWidth: {
            top: 2,
            right: 2,
            bottom: 2,
            left: 2
          },
        };
      });

    this.mappedChartDatasetsReady = true;

    this.changeDetector.detectChanges();
  }

  private computeColor(
    value: number,
    max: number,
  ): string {

    const minOpacity = 0.05;

    let relative = value / max;

    if (relative > 1) {
      relative = 1;
    }

    const opacity = minOpacity + relative * (1 - minOpacity);

    return `rgba(${this.baseColor}, ${opacity})`;
  }

  private keepCanvasSizeSameAsChartContainer(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (!this.chartContainer
      || !this.initializedView) {
      return;
    }

    this.chartContainerResizeObserver =
      new ResizeObserver((entries) => {

        for (const entry of entries) {

          if (entry.target === this.chartContainer) {

            let {width, height} = entry.contentRect;

            width = Math.round(width);
            height = Math.round(height);

            if (this.canvasContainer && height > 0 && width > 0) {

              this.canvasContainer.nativeElement.style.width = `${width}px`;
              this.canvasContainer.nativeElement.style.height = `${height}px`;
            }
          }
        }
      });

    this.chartContainerResizeObserver.observe(this.chartContainer);
  }

  private formatValue(
    value: any,
  ): string {

    if (this.valueFormat === 'custom') {
      return this.valueFormatter(value);
    }

    if (this.valueFormat === 'human-readable-time') {

      const seconds = Number(value) / 1000;

      return this.readableSecondsPipe.transform(seconds, 1) || '';
    }

    return String(value);
  }

}
